<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch } from "vue";
import { listInventory, getCategories } from "../db/inventory.js";
import { addToCart } from "../store/cart.js";
import ProductDetailModal from "../components/ProductDetailModal.vue";
import ChatAssistant from "../components/ChatAssistant.vue";

const loading = ref(false);
const products = ref([]);
const search = ref("");
// Filtros avanzados
const sortKey = ref("relevance"); // relevance | price_asc | price_desc
const minPrice = ref("");
const maxPrice = ref("");
const onlyFeatured = ref(false);
// Filtro por categoría
const categories = ref([]);
const selectedCategory = ref("");

function clearFilters() {
  sortKey.value = "relevance";
  minPrice.value = "";
  maxPrice.value = "";
  onlyFeatured.value = false;
  selectedCategory.value = "";
}
const showDetail = ref(false);
const selectedId = ref(null);
const justAdded = ref({});
const page = ref(1);
const pageSize = 20;
const totalPages = computed(() => Math.max(1, Math.ceil(filteredProducts.value.length / pageSize)));
const pageItems = computed(() => {
  const start = (page.value - 1) * pageSize;
  return filteredProducts.value.slice(start, start + pageSize);
});

onMounted(async () => {
  loading.value = true;
  try {
    const rows = await listInventory();
    products.value = rows.map((r) => ({
      id: r.id,
      name: r.name,
      description: r.description || null,
      // Precio final con descuento y IVA (si aplica)
      price:
        (Number(r.unit_price || 0) *
          (1 - Math.min(Math.max(Number(r.discount || 0), 0), 100) / 100)) *
        (r.tax_rate != null ? 1 + Number(r.tax_rate) / 100 : 1),
      image_base64: r.image_base64 || null,
      category: r.category || null,
      stock_qty: Number(r.stock_qty || 0),
      discount: Number(r.discount || 0),
      created_at: r.created_at || null,
      isNew: isNew(r.created_at),
      isFeatured: Number(r.discount || 0) >= 10 || isNew(r.created_at),
    }));
    // Cargar categorías desde la base de datos
    try {
      const cats = await getCategories();
      categories.value = (cats || []).map((c) => ({ id: c.id, name: c.name }));
    } catch { }
  } finally {
    loading.value = false;
  }
  // Escuchar búsqueda enviada desde la nav
  try {
    const handler = (e) => {
      search.value = String(e.detail || "");
    };
    window.addEventListener("product-search", handler);
    // Guardar referencia para cleanup
    cleanup.value = () => window.removeEventListener("product-search", handler);
  } catch { }
});

const cleanup = ref(null);
onBeforeUnmount(() => {
  try {
    cleanup.value && cleanup.value();
  } catch { }
});

const filteredProducts = computed(() => {
  const q = search.value.trim().toLowerCase();
  let list = !q
    ? products.value.slice()
    : products.value.filter(
      (p) =>
        (p.name || "").toLowerCase().includes(q) ||
        String(p.category || "")
          .toLowerCase()
          .includes(q)
    );
  // Filtro por categoría seleccionada
  if (selectedCategory.value) {
    const cat = String(selectedCategory.value).toLowerCase();
    list = list.filter((p) => String(p.category || "").toLowerCase() === cat);
  }
  // Filtro por rango de precios
  const min = minPrice.value !== "" ? Number(minPrice.value) : null;
  const max = maxPrice.value !== "" ? Number(maxPrice.value) : null;
  if (min !== null) list = list.filter((p) => p.price >= min);
  if (max !== null) list = list.filter((p) => p.price <= max);
  // Solo destacados
  if (onlyFeatured.value) list = list.filter((p) => !!p.isFeatured);
  // Orden
  if (sortKey.value === "price_asc") list.sort((a, b) => a.price - b.price);
  else if (sortKey.value === "price_desc")
    list.sort((a, b) => b.price - a.price);
  else {
    // relevance: nuevos y con descuento primero
    list.sort((a, b) => Number(b.isFeatured) - Number(a.isFeatured));
  }
  return list;
});

watch(filteredProducts, () => { page.value = 1; });

function add(p) {
  addToCart({
    id: p.id,
    name: p.name,
    unit_price: p.price,
    image_base64: p.image_base64,
  });

  // Feedback visual: verde temporal y texto "producto agregado.."
  justAdded.value[p.id] = true;
  setTimeout(() => {
    delete justAdded.value[p.id];
  }, 1500);
}

function open(p) {
  selectedId.value = p.id;
  showDetail.value = true;
}
function closeModal() {
  showDetail.value = false;
}

function gotoPage(p) {
  if (p < 1) p = 1;
  if (p > totalPages.value) p = totalPages.value;
  page.value = p;
}
function prevPage() { gotoPage(page.value - 1); }
function nextPage() { gotoPage(page.value + 1); }

function isNew(createdAt) {
  if (!createdAt) return false;
  try {
    const created = new Date(String(createdAt).replace(" ", "T"));
    const now = new Date();
    return (
      created.getFullYear() === now.getFullYear() &&
      created.getMonth() === now.getMonth() &&
      created.getDate() === now.getDate()
    );
  } catch {
    return false;
  }
}

function formatDesc(text) {
  const s = String(text || "");
  return s.replace(/¡[^!]+!/g, (m) => `<b>${m}</b>`);
}

function defaultProductImage(p) {
  const name = String(p?.name || "Producto").slice(0, 24);
  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300">
  <rect width="100%" height="100%" fill="#f1f5f9"/>
  <rect x="20" y="20" width="360" height="260" rx="12" fill="#e2e8f0" stroke="#cbd5e1"/>
  <text x="200" y="160" text-anchor="middle" font-family="Outfit, sans-serif" font-size="20" fill="#64748b">SIN IMAGEN</text>
  <text x="200" y="190" text-anchor="middle" font-family="Outfit, sans-serif" font-size="14" fill="#94a3b8">${name}</text>
</svg>`;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
}
</script>

<template>
  <main class="wrap">
    <header class="top">
      <h1 class="title">Productos</h1>
      <p class="subtitle">Elige productos y agrégalos al carrito</p>
      <!-- Filtros avanzados -->
      <div class="filters" aria-label="Filtros de productos">
        <div class="field">
          <label class="label">
            <span class="material-symbols-rounded label-icon">category</span>
            Categoría
          </label>
          <div class="control">
            <span class="material-symbols-rounded icon">label</span>
            <select v-model="selectedCategory" class="select">
              <option value="">Todas</option>
              <option v-for="c in categories" :key="c.id" :value="c.name">
                {{ c.name }}
              </option>
            </select>
          </div>
        </div>
        <div class="field">
          <label class="label">
            <span class="material-symbols-rounded label-icon">swap_vert</span>
            Ordenar por
          </label>
          <div class="control">
            <span class="material-symbols-rounded icon">tune</span>
            <select v-model="sortKey" class="select">
              <option value="relevance">Relevancia</option>
              <option value="price_asc">Precio menor</option>
              <option value="price_desc">Precio mayor</option>
            </select>
          </div>
        </div>
        <div class="field">
          <label class="label">
            <span class="material-symbols-rounded label-icon">attach_money</span>
            Precio mínimo
          </label>
          <div class="control">
            <span class="material-symbols-rounded icon">sell</span>
            <input class="input" type="number" min="0" step="0.01" v-model="minPrice" placeholder="0.00" />
          </div>
        </div>
        <div class="field">
          <label class="label">
            <span class="material-symbols-rounded label-icon">price_change</span>
            Precio máximo
          </label>
          <div class="control">
            <span class="material-symbols-rounded icon">price_check</span>
            <input class="input" type="number" min="0" step="0.01" v-model="maxPrice" placeholder="9999" />
          </div>
        </div>
        <div class="field field--center">
          <label class="label">
            <span class="material-symbols-rounded label-icon">star</span>
            Solo destacados
          </label>
          <div class="control switch-control">
            <input type="checkbox" v-model="onlyFeatured" class="switch-input" />
          </div>
        </div>
        <div class="actions">
          <button type="button" class="btn clear-filters" @click="clearFilters" title="Limpiar filtros">
            <span class="material-symbols-rounded">cleaning_services</span>
            Limpiar filtros
          </button>
        </div>
      </div>
    </header>
    <section v-if="loading" class="muted">Cargando productos…</section>
    <section v-else class="grid">
      <article v-for="p in pageItems" :key="p.id" class="card" :title="p.name" @click="open(p)">
        <span v-if="p.isNew" class="badge-new">Nuevo</span>
        <span v-if="p.isFeatured && !p.isNew" class="badge-featured">★ Destacado</span>
        <div class="thumb">
          <img :src="p.image_base64 ? `data:image/jpeg;base64,${p.image_base64}` : defaultProductImage(p)"
            alt="Imagen" />
        </div>
        <h3 class="name">{{ p.name }}</h3>
        <p class="desc" v-if="p.description" v-html="formatDesc(p.description)"></p>
        <p class="cat">Categoría: {{ p.category || 'General' }}</p>
        <div class="foot">
          <div class="price">
            <span class="price-label">Precio:</span>
            <span class="price-value">${{ p.price.toFixed(2) }}</span>
          </div>
          <button class="btn add" :class="{ success: justAdded[p.id] }" @click.stop="add(p)" title="Agregar al carrito">
            <span class="material-symbols-rounded">shopping_bag</span>
            {{ justAdded[p.id] ? "producto agregado.." : "Agregar al carrito" }}
          </button>
        </div>
      </article>
    </section>
    <div class="pagination" v-if="totalPages > 1">
      <button class="pager-btn" :disabled="page <= 1" @click="prevPage">
        <span class="material-symbols-rounded" aria-hidden="true">chevron_left</span>
      </button>
      <span class="pager-info">Página {{ page }} de {{ totalPages }}</span>
      <button class="pager-btn" :disabled="page >= totalPages" @click="nextPage">
        <span class="material-symbols-rounded" aria-hidden="true">chevron_right</span>
      </button>
    </div>

    <ProductDetailModal v-if="showDetail" :product-id="selectedId" :open="showDetail" @close="closeModal" />
    <ChatAssistant />
  </main>
</template>

<style scoped>
.wrap {
  background: linear-gradient(135deg, #0a0b14 0%, #1a1610 100%);
  min-height: 100vh;
  display: grid;
  gap: 1rem;
  padding: 1rem;
}

.top {
  display: grid;
  gap: 0.2rem;
}

.title {
  font-weight: 800;
  font-size: 1.2rem;
}

.subtitle {
  color: var(--muted);
}

.filters {
  display: grid;
  grid-template-columns: repeat(6, minmax(160px, auto));
  gap: 1.6rem;
  /* mayor separación para que no choquen */
  align-items: center;
  margin-top: 0.6rem;
  padding: 0.8rem 1rem;
  /* un poco más de padding */
  border-radius: 18px;
  background: linear-gradient(180deg,
      rgba(255, 255, 255, 0.14),
      rgba(255, 255, 255, 0.06));
  border: 1px solid rgba(255, 255, 255, 0.25);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  backdrop-filter: saturate(1.4) blur(10px);
  overflow-x: auto;
  /* mantener una sola fila con scroll si es necesario */
}

.field {
  display: grid;
  gap: 0.35rem;
  min-width: 0;
}

.field--center {
  justify-items: center;
}

.field--center .label {
  display: flex;
  justify-content: center;
  width: 100%;
  text-align: center;
}

.label {
  color: var(--muted);
  font-size: 0.74rem;
  /* más compacto */
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-weight: 600;
}

.label-icon {
  font-size: 18px;
  color: var(--muted);
}

.control {
  position: relative;
  overflow: visible;
}

.icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 20px;
  color: var(--muted);
  pointer-events: none;
}

.select,
.input {
  width: 100%;
  border: 1px solid var(--border);
  background: radial-gradient(100% 100% at 50% 0%,
      var(--surface) 0%,
      rgba(255, 255, 255, 0.02) 100%);
  color: var(--text);
  border-radius: 14px;
  padding: 0.6rem 0.85rem 0.6rem 2.6rem;
  /* más padding para que no choquen con el ícono */
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.1);
  transition: box-shadow 160ms ease, transform 160ms ease,
    border-color 160ms ease;
  box-sizing: border-box;
  /* evita que el padding cause desbordes */
}

.select:focus,
.input:focus {
  outline: none;
  border-color: rgba(198, 146, 61, 0.6);
  box-shadow: 0 14px 30px rgba(198, 146, 61, 0.3);
}

.select {
  appearance: none;
  background-image: linear-gradient(45deg, transparent 50%, var(--muted) 50%),
    linear-gradient(135deg, var(--muted) 50%, transparent 50%);
  background-position: calc(100% - 18px) calc(50% - 2px),
    calc(100% - 12px) calc(50% - 2px);
  background-size: 6px 6px, 6px 6px;
  background-repeat: no-repeat;
  cursor: pointer;
}

.switch-row {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.65rem 0.9rem;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: linear-gradient(180deg,
      var(--surface),
      rgba(255, 255, 255, 0.06));
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.1);
  min-width: 0;
  /* permitir que se contraiga dentro de su columna */
  width: 100%;
}

.switch-input {
  appearance: none;
  width: 42px;
  height: 24px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: #e5e7eb;
  position: relative;
  transition: background 160ms ease, border-color 160ms ease;
  cursor: pointer;
}

.switch-input::after {
  content: "";
  position: absolute;
  left: 3px;
  top: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: white;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.18);
  transition: transform 160ms ease;
}

.switch-input:checked {
  background: #10b98133;
  border-color: #10b981;
}

.switch-input:checked::after {
  transform: translateX(18px);
}

.switch-control {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.actions {
  display: flex;
  align-items: center;
}

.btn.clear-filters {
  border: none;
  background: #ef4444;
  color: white;
  border-radius: 999px;
  padding: 0.55rem 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(239, 68, 68, 0.28);
  transition: transform 160ms ease, box-shadow 160ms ease, filter 160ms ease;
}

.btn.clear-filters:hover {
  filter: brightness(1.06);
  transform: translateY(-1px);
}

.btn.clear-filters:active {
  transform: translateY(0);
}

.grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.2rem;
}

.pager-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.4rem 0.6rem;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  cursor: pointer;
}

.pager-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pager-info {
  font-weight: 600;
}

.card {
  position: relative;
  display: grid;
  gap: 0.6rem;
  border: none;
  border-radius: 18px;
  padding: 0.9rem;
  background: var(--surface);
  cursor: pointer;
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.14);
  transition: transform 160ms ease, box-shadow 160ms ease;
}

.card:hover {
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.18);
  transform: translateY(-1px);
}

.thumb img {
  width: 100%;
  height: auto;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 12px;
  transition: transform 220ms ease;
  will-change: transform;
}

.thumb {
  position: relative;
  overflow: hidden;
  border-radius: 12px;
}

.card:hover .thumb img {
  transform: scale(1.06);
}

.name {
  font-weight: 700;
}

.desc {
  color: var(--muted);
  font-size: 0.92rem;
  margin: -0.2rem 0 0.1rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  /* muestra 2 líneas máximo */
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.cat {
  color: var(--muted);
  font-size: 0.9rem;
}

.foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.price {
  display: grid;
  grid-template-rows: auto auto;
  gap: 2px;
}

.price-label {
  color: var(--muted);
  font-size: 0.9rem;
  font-weight: 700;
}

.price-value {
  font-weight: 700;
}

.badge-featured {
  position: absolute;
  top: 10px;
  right: 10px;
  background: #f59e0b;
  color: #1a1a1a;
  border-radius: 999px;
  padding: 0.15rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 800;
  box-shadow: 0 4px 10px rgba(245, 158, 11, 0.25);
  z-index: 5;
}

.btn.add {
  border: none;
  background: linear-gradient(135deg, #C6923D 0%, #DFC05E 100%);
  color: #000000;
  border-radius: 999px;
  padding: 0.4rem 0.7rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  cursor: pointer;
  transition: transform 160ms ease, box-shadow 160ms ease, filter 160ms ease;
}

.btn.add:hover {
  filter: brightness(1.06);
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(223, 192, 94, 0.28);
}

.btn.add.success {
  background: #10b981;
  box-shadow: 0 10px 20px rgba(16, 185, 129, 0.28);
}

.btn.add:active {
  transform: translateY(0);
  box-shadow: 0 6px 14px rgba(223, 192, 94, 0.22);
}

.btn.add .material-symbols-rounded {
  font-size: 20px;
}

.badge-new {
  position: absolute;
  top: 10px;
  left: 10px;
  background: #10b981;
  color: white;
  border-radius: 999px;
  padding: 0.15rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  box-shadow: 0 4px 10px rgba(16, 185, 129, 0.25);
  z-index: 5;
}

/* Responsivo: mantener proporción pero ajustar # de columnas */
@media (max-width: 1500px) {
  .grid {
    grid-template-columns: repeat(5, 1fr);
  }
}

@media (max-width: 1200px) {
  .grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (max-width: 900px) {
  .grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 700px) {
  .grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .grid {
    grid-template-columns: 1fr;
  }
}

/* Responsive del contenedor de filtros */
@media (max-width: 1280px) {
  .filters {
    grid-template-columns: repeat(6, minmax(160px, auto));
  }
}

@media (max-width: 960px) {
  .filters {
    grid-template-columns: repeat(6, minmax(160px, auto));
  }
}

@media (max-width: 620px) {
  .filters {
    grid-template-columns: repeat(6, minmax(160px, auto));
  }
}

/* Compact size overrides for inputs and controls */
.filters .icon {
  font-size: 14px;
  left: 10px;
}

.filters .select,
.filters .input {
  border-radius: 10px;
  padding: 0.35rem 0.6rem 0.35rem 1.9rem;
  font-size: 0.85rem;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.08);
}

.switch-input {
  width: 34px;
  height: 18px;
}

.switch-input::after {
  left: 2px;
  top: 2px;
  width: 14px;
  height: 14px;
}

.switch-input:checked::after {
  transform: translateX(16px);
}

.btn.clear-filters {
  padding: 0.4rem 0.7rem;
  box-shadow: 0 6px 14px rgba(239, 68, 68, 0.24);
}

/* Layout fix: prevent grid items from overflowing their columns */
.filters>* {
  min-width: 0;
  box-sizing: border-box;
}

.filters .field {
  min-width: 0;
  max-width: 220px;
}

.filters .switch-row {
  min-width: 0;
  width: 100%;
  max-width: 220px;
}

/* Dark-mode dropdown visibility for native <select> menus */
@media (prefers-color-scheme: dark) {
  .filters .select {
    background-color: #0f172a;
    border-color: #334155;
    color: #e5e7eb;
    color-scheme: dark;
    /* flecha del select más visible en dark */
    background-image: linear-gradient(45deg, transparent 50%, #94a3b8 50%),
      linear-gradient(135deg, #94a3b8 50%, transparent 50%);
  }

  .filters .select option,
  .filters .select optgroup {
    background-color: #0f172a;
    /* very dark background */
    color: #e5e7eb;
    /* light text */
  }

  .filters .select option:hover,
  .filters .select option:checked {
    background-color: #1f2937;
    /* hover/selected */
    color: #ffffff;
  }

  /* icono dentro del control más tenue en dark */
  .filters .icon {
    color: #94a3b8;
  }
}

/* En modo claro, regresa el esquema a light para que el sistema dibuje el menú adecuado */
@media (prefers-color-scheme: light) {
  .filters .select {
    color-scheme: light;
    background-color: #ffffff;
    border-color: #e5e7eb;
    color: #1f2937;
  }

  .filters .select option {
    background-color: #ffffff;
    color: #1f2937;
  }
}

:root[data-theme="light"] .filters .select {
  color-scheme: light;
  background-color: #ffffff;
  border-color: #e5e7eb;
  color: #1f2937;
}

:root[data-theme="light"] .filters .select option {
  background-color: #ffffff;
  color: #1f2937;
}

:root[data-theme="dark"] .filters .select {
  background-color: #0f172a;
  border-color: #334155;
  color: #e5e7eb;
  color-scheme: dark;
  background-image: linear-gradient(45deg, transparent 50%, #94a3b8 50%),
    linear-gradient(135deg, #94a3b8 50%, transparent 50%);
}

:root[data-theme="dark"] .filters .select option,
:root[data-theme="dark"] .filters .select optgroup {
  background-color: #0f172a;
  color: #e5e7eb;
}

:root[data-theme="dark"] .filters .icon {
  color: #94a3b8;
}
</style>
